// src/jornada/finalizarJornada.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { jornadaStore } from "../data/jornadaStore";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, Stack, Button } from "@mui/material";

export const FinalizarJornada = ({
  jornada,
  onJornadaActualizada,
  onSuccess,
  onError,
  uiLocked = false,
}) => {
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [centroCoords, setCentroCoords] = useState(null); // { lat, lon }
  const [centroNombre, setCentroNombre] = useState("");

  const clickedRef = useRef(false); // anti-doble click

  // Normaliza campos de la jornada (puede ser “local:*” si nació offline)
  const info = useMemo(() => {
    if (!jornada) return null;

    const latCentro =
      jornada?.centro?.latitud ??
      jornada?.centro_latitud ??
      jornada?.latitud_centro ??
      jornada?.centroLat;

    const lonCentro =
      jornada?.centro?.longitud ??
      jornada?.centro_longitud ??
      jornada?.longitud_centro ??
      jornada?.centroLon;

    return {
      id: jornada.id,
      esLocal: String(jornada.id || "").startsWith("local"),
      centroId: jornada.centro_id ?? jornada?.centro?.id ?? null,
      centro: jornada?.centro?.nombre ?? jornada?.centro_nombre ?? "",
      inicio: jornada.fecha_inicio ?? jornada.inicio ?? jornada.created_at ?? null,
      latCentro: latCentro != null ? Number(latCentro) : null,
      lonCentro: lonCentro != null ? Number(lonCentro) : null,
    };
  }, [jornada]);

  // Completa coords/nombre del centro usando la store si faltan (offline-first)
  useEffect(() => {
    let alive = true;

    const hydrateCentro = async () => {
      if (!info?.id) return;

      // si ya venían coords/nombre, listo
      if (info.latCentro != null && info.lonCentro != null) {
        if (alive) setCentroCoords({ lat: info.latCentro, lon: info.lonCentro });
        if (alive && info.centro?.trim()) setCentroNombre(info.centro);
        return;
      }

      if (!info.centroId) return;

      try {
        const centros = await jornadaStore.getCentros();
        const c = Array.isArray(centros)
          ? centros.find((z) => Number(z.id) === Number(info.centroId))
          : null;

        if (!c) return;

        if (c.latitud != null && c.longitud != null) {
          if (alive) setCentroCoords({ lat: Number(c.latitud), lon: Number(c.longitud) });
        }
        if (c.nombre && !info.centro?.trim()) {
          if (alive) setCentroNombre(c.nombre);
        }
      } catch (e) {
        console.warn("[FinalizarJornada] getCentros:", e?.message);
      }
    };

    hydrateCentro();
    return () => {
      alive = false;
    };
  }, [info?.id, info?.centroId, info?.latCentro, info?.lonCentro, info?.centro]);

  const distanciaMetros = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const displayCentro =
    info?.centro?.trim()
      ? info.centro
      : centroNombre || (info?.centroId ? `Centro ${info.centroId}` : "Centro no disponible");

  const botonDeshabilitado =  loading;

  const finalizarConUbicacion = async () => {
    if (clickedRef.current) return;
    clickedRef.current = true;

    if (!navigator.geolocation) {
      clickedRef.current = false;
      onError?.("Tu navegador no soporta geolocalización.");
      toast.error("Tu navegador no soporta geolocalización.");
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const latFin = pos.coords.latitude;
          const lonFin = pos.coords.longitude;

          let metros = 0;
          let enRango = true;

          if (centroCoords?.lat != null && centroCoords?.lon != null) {
            metros = distanciaMetros(latFin, lonFin, centroCoords.lat, centroCoords.lon);

            const UMBRAL_M = 100; // UX, la validación real vive en store/backend
            enRango = metros <= UMBRAL_M;

            if (!enRango) {
              const km = (metros / 1000).toFixed(2);
              const ok = window.confirm(
                `Estás a ${km} km del centro. ¿Deseas finalizar igualmente?`
              );
              if (!ok) {
                toast.info("Acción cancelada.");
                setLoading(false);
                clickedRef.current = false;
                return;
              }
            }
          }

          const result = await jornadaStore.finish({
            latitud: latFin,
            longitud: lonFin,
            enRango,
            metros,
          });

          if (result?.status === "noop") {
            onError?.("No hay ninguna jornada activa o ya está pendiente de envío.");
            toast.warning("No hay ninguna jornada activa o ya está pendiente de envío.");
          } else if (result?.status === "online") {
            onSuccess?.("Jornada finalizada correctamente.");
            toast.success("Jornada finalizada correctamente.");
          } else {
            onSuccess?.("Sin conexión: cierre guardado y se enviará automáticamente.");
            toast.info("Sin conexión: cierre guardado y se enviará automáticamente.");
          }

          await onJornadaActualizada?.();
        } catch (e) {
          console.error(e);
          onError?.("No se pudo finalizar la jornada.");
          toast.error("No se pudo finalizar la jornada.");
        } finally {
          setLoading(false);
          clickedRef.current = false;
        }
      },
      (err) => {
        console.error(err);
        onError?.("No se pudo obtener la ubicación al cerrar.");
        toast.error("No se pudo obtener la ubicación al cerrar.");
        setLoading(false);
        clickedRef.current = false;
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const cancelarJornada = async () => {
    if (clickedRef.current || uiLocked) return;
    clickedRef.current = true;

    try {
      setLoading(true);

      const ok = window.confirm("¿Seguro que quieres cancelar la jornada activa?");
      if (!ok) {
        toast.info("Acción cancelada.");
        return;
      }

      const result = await jornadaStore.cancel();

      if (result?.status === "noop") {
        onError?.("No hay ninguna jornada activa o ya está pendiente de envío.");
        toast.warning("No hay ninguna jornada activa o ya está pendiente de envío.");
      } else if (result?.status === "online") {
        onSuccess?.("Jornada cancelada correctamente.");
        toast.success("Jornada cancelada correctamente.");
      } else {
        onSuccess?.("Sin conexión: cancelación guardada y se enviará automáticamente.");
        toast.info("Sin conexión: cancelación guardada y se enviará automáticamente.");
      }

      await onJornadaActualizada?.();
    } catch (e) {
      console.error(e);
      onError?.("No se pudo cancelar la jornada.");
      toast.error("No se pudo cancelar la jornada.");
    } finally {
      setLoading(false);
      clickedRef.current = false;
    }
  };

  // =========================
  // Render
  // =========================
  if (!info?.id) {
    return (
      <Box className="appContainer">
        <Stack spacing={2}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Jornada activa
          </Typography>

          <Box className="contentRowCenter">
            <Box className="contentContainer contentContainer--formSingle">
              <Paper sx={{ p: 2 }}>
                <Typography>No hay ninguna jornada activa.</Typography>
              </Paper>
            </Box>
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        {/* ✅ Encabezado homogéneo */}
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Jornada activa
        </Typography>

        {/* ✅ “tabla” de una fila, centrada y estrecha */}
        <Box className="contentRowCenter">
          <Box className="contentContainer contentContainer--formSingle">
            <Paper sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  alignItems: "center",
                  gridTemplateColumns: { xs: "1fr", md: "2fr 2fr auto" },
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    Centro
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>{displayCentro}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    Inicio
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    {info.inicio ? new Date(info.inicio).toLocaleString() : "Fecha no disponible"}
                  </Typography>
                </Box>

                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ justifyContent: { xs: "flex-end", md: "flex-end" }, flexWrap: "wrap" }}
                >
                  <Button
                    onClick={cancelarJornada}
                    variant="outlined"
                    disabled={botonDeshabilitado}
                  >
                    Cancelar
                  </Button>

                  <Button onClick={finalizarConUbicacion} disabled={botonDeshabilitado}>
                    {loading ? "Procesando..." : "Finalizar"}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
